import api from './api';
import {
  AuditLog,
  AuditLogFilters,
  AuditLogsResponse,
  AuditStats,
} from '../types/audit';

/**
 * Audit Log Service
 * API calls for audit logging
 */

/**
 * Get audit logs with filters
 */
export const getAuditLogs = async (
  filters?: AuditLogFilters
): Promise<AuditLogsResponse> => {
  const params = new URLSearchParams();

  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.action) params.append('action', filters.action);
  if (filters?.entityType) params.append('entityType', filters.entityType);
  if (filters?.entityId) params.append('entityId', filters.entityId);
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.offset) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const response = await api.get<{ success: boolean; data: AuditLogsResponse }>(
    `/audit${queryString ? `?${queryString}` : ''}`
  );
  return response.data.data;
};

/**
 * Get audit log statistics
 */
export const getAuditStats = async (
  startDate?: string,
  endDate?: string
): Promise<AuditStats> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const queryString = params.toString();
  const response = await api.get<{ success: boolean; data: AuditStats }>(
    `/audit/stats${queryString ? `?${queryString}` : ''}`
  );
  return response.data.data;
};

/**
 * Get recent audit activity
 */
export const getRecentAuditActivity = async (
  limit?: number
): Promise<AuditLog[]> => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());

  const queryString = params.toString();
  const response = await api.get<{ success: boolean; data: AuditLog[] }>(
    `/audit/recent${queryString ? `?${queryString}` : ''}`
  );
  return response.data.data;
};

/**
 * Export audit logs to CSV
 * Returns the download URL
 */
export const exportAuditLogs = async (filters?: AuditLogFilters): Promise<string> => {
  const params = new URLSearchParams();

  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.action) params.append('action', filters.action);
  if (filters?.entityType) params.append('entityType', filters.entityType);
  if (filters?.entityId) params.append('entityId', filters.entityId);
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (filters?.search) params.append('search', filters.search);

  const queryString = params.toString();
  const url = `/audit/export${queryString ? `?${queryString}` : ''}`;

  // For CSV export, we need to use the full API URL with auth token
  const token = localStorage.getItem('accessToken');
  const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  // Return the full URL with token for download
  return `${baseURL}${url}&token=${token}`;
};

/**
 * Download audit logs CSV (triggers browser download)
 */
export const downloadAuditLogs = async (filters?: AuditLogFilters): Promise<void> => {
  const params = new URLSearchParams();

  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.action) params.append('action', filters.action);
  if (filters?.entityType) params.append('entityType', filters.entityType);
  if (filters?.entityId) params.append('entityId', filters.entityId);
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (filters?.search) params.append('search', filters.search);

  const queryString = params.toString();

  // Make request with response type blob
  const response = await api.get(`/audit/export${queryString ? `?${queryString}` : ''}`, {
    responseType: 'blob',
  });

  // Create blob URL and trigger download
  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
